<template>
	<div
		class="g-header-hero-preview"
		:class="{
			'g-header-hero-preview--h5': h5,
			'g-header-hero-preview--compact': compact,
		}"
		:style="rootStyle"
		@click="onClick"
	>
		<img
			v-if="showBackgroundImage"
			:src="bgImageUrl"
			class="g-header-hero-preview__bg"
			alt=""
		/>
		<div
			v-if="normalizedData.darkOverlay && showBackgroundImage"
			class="g-header-hero-preview__overlay"
		></div>
		<div class="g-header-hero-preview__content">
			<img
				v-if="normalizedData.showLogo && logoUrl"
				:src="logoUrl"
				class="g-header-hero-preview__logo"
				alt=""
			/>
			<div
				v-if="normalizedData.title"
				class="g-header-hero-preview__title"
				:style="{ color: normalizedData.titleColor }"
			>{{ normalizedData.title }}</div>
			<div
				v-if="normalizedData.subtitle"
				class="g-header-hero-preview__subtitle"
				:style="{ color: normalizedData.subtitleColor }"
			>{{ normalizedData.subtitle }}</div>
			<div
				v-if="normalizedData.buttonText"
				class="g-header-hero-preview__btn"
				:style="buttonStyle"
			>{{ normalizedData.buttonText }}</div>
		</div>
	</div>
</template>

<script>
	import { navigateJumpLink } from '../utils/jumpLink.js';
	import {
		getHeaderHeroImageUrl,
		normalizeHeaderHeroData,
	} from '../utils/headerHero.js';

	export default {
		name: 'GHeaderHeroPreview',
		props: {
			data: {
				type: Object,
				default: () => ({}),
			},
			h5: {
				type: Boolean,
				default: false,
			},
			compact: {
				type: Boolean,
				default: false,
			},
			interactive: {
				type: Boolean,
				default: true,
			},
		},
		computed: {
			normalizedData() {
				return normalizeHeaderHeroData(this.data);
			},
		rootStyle() {
			const height = this.normalizedData.height;
			const radius = this.normalizedData.borderRadius;
			const unit = this.h5 ? 'rem' : 'px';
			const heightValue = this.h5 ? height / 100 : height;
			const radiusValue = this.h5 ? radius / 50 : radius;
			return {
				'--g-header-hero-height': `${heightValue}${unit}`,
				'--g-header-hero-radius': `${radiusValue}${unit}`,
				'--g-header-hero-ratio': this.h5 ? `570 / ${height}` : null,
			};
		},
			showBackgroundImage() {
				return this.normalizedData.showBg && !!this.bgImageUrl;
			},
			bgImageUrl() {
				return getHeaderHeroImageUrl(this.normalizedData.image);
			},
			logoUrl() {
				return getHeaderHeroImageUrl(this.normalizedData.logo);
			},
			buttonStyle() {
				return {
					background: this.normalizedData.buttonBgColor,
					color: this.normalizedData.buttonTextColor,
				};
			},
		},
		methods: {
			onClick() {
				if (!this.h5 || !this.interactive || !this.normalizedData.jumpLink?.type) return;
				navigateJumpLink(this.normalizedData.jumpLink, {
					apiRequest: this.$apiRequest,
				});
			},
		},
	};
</script>

<style scoped lang="less">
	.g-header-hero-preview {
		position: relative;
		width: 100%;
		height: var(--g-header-hero-height, 360px);
		overflow: hidden;
		border-radius: 0 0 var(--g-header-hero-radius, 30px) var(--g-header-hero-radius, 30px);

		&--h5 {
			height: auto;
			aspect-ratio: var(--g-header-hero-ratio, 1.5833);
		}
	}

	.g-header-hero-preview__bg {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.g-header-hero-preview__overlay {
		position: absolute;
		inset: 0;
		z-index: 1;
		background: rgba(0, 0, 0, 0.35);
		pointer-events: none;
	}

	.g-header-hero-preview__content {
		position: absolute;
		inset: 0;
		z-index: 2;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 24px 28px;
		box-sizing: border-box;
	}

	.g-header-hero-preview__logo {
		max-width: 120px;
		max-height: 48px;
		margin-bottom: 12px;
		object-fit: contain;
	}

	.g-header-hero-preview__title {
		max-width: 100%;
		font-size: 32px;
		font-weight: 700;
		line-height: 1.4;
		text-align: center;
		word-break: break-word;
	}

	.g-header-hero-preview__subtitle {
		max-width: 100%;
		margin-top: 10px;
		font-size: 22px;
		font-weight: 400;
		line-height: 1.4;
		text-align: center;
		word-break: break-word;
	}

	.g-header-hero-preview__btn {
		display: inline-block;
		margin-top: 20px;
		padding: 10px 28px;
		border-radius: 999px;
		font-size: 15px;
		font-weight: 700;
		line-height: 1.4;
		text-align: center;
		white-space: nowrap;
		box-sizing: border-box;
	}

	.g-header-hero-preview--h5 {
		border-radius: 0 0 var(--g-header-hero-radius, 0.3rem) var(--g-header-hero-radius, 0.3rem);
		.g-header-hero-preview__logo {
			max-width: 2.4rem;
			max-height: 0.96rem;
			margin-bottom: 0.24rem;
		}

		.g-header-hero-preview__title {
			font-size: 0.48rem;
		}

		.g-header-hero-preview__subtitle {
			margin-top: 0.16rem;
			font-size: 0.38rem;
		}

		.g-header-hero-preview__btn {
			margin-top: 0.32rem;
			padding: 0.16rem 0.48rem;
			font-size: 0.28rem;
		}

		.g-header-hero-preview__content {
			padding: 0.32rem 0.4rem;
		}
	}

	.g-header-hero-preview--compact {
		height: 192px;

		.g-header-hero-preview__logo {
			max-width: 64px;
			max-height: 28px;
			margin-bottom: 6px;
		}

		.g-header-hero-preview__title {
			font-size: 17px;
		}

		.g-header-hero-preview__subtitle {
			margin-top: 6px;
			font-size: 14px;
		}

		.g-header-hero-preview__btn {
			margin-top: 16px;
			padding: 8px 24px;
			font-size: 14px;
		}

		.g-header-hero-preview__content {
			padding: 10px 12px;
		}
	}
</style>
